import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, useAuth } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Plus, 
  Search, 
  FolderOpen, 
  MoreVertical,
  Edit,
  Trash2,
  Eye,
  ArrowRight,
  UserCog,
  Pencil,
  EyeOff,
  AlertCircle,
  Undo2,
  Download,
  GitBranch,
  Users,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import CaseLocationFields from '../components/CaseLocationFields';
import AssignCaseDialog from '../components/AssignCaseDialog';

const CasesPage = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCase, setEditingCase] = useState(null);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  const [formData, setFormData] = useState({
    case_name: '',
    client_name: '',
    property_reference: '',
    report_date: '',
    addressee_address: '',
    status: 'active',
    case_location: { district: '', taluk: '', hobli: '', village: '' }
  });
  const [submitting, setSubmitting] = useState(false);
  // Soft-delete state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  // User-wise Cases (Super Admin only)
  const [usersList, setUsersList] = useState([]);
  const [viewUserId, setViewUserId] = useState('');

  useEffect(() => {
    fetchCases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDeleted, viewUserId]);

  useEffect(() => {
    if (isAdmin) fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const fetchUsers = async () => {
    try {
      const r = await api.get('/users');
      setUsersList((r.data || []).filter((u) => !u.deleted_at));
    } catch (e) {
      // non-blocking
    }
  };

  const fetchCases = async () => {
    try {
      const response = await api.get('/cases', {
        params: {
          include_deleted: showDeleted ? 'true' : 'false',
          ...(viewUserId ? { user_id: viewUserId } : {}),
        },
      });
      setCases(response.data);
    } catch (error) {
      toast.error('Failed to fetch cases');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    // Normalize case_location: send null if all fields are empty
    const loc = formData.case_location || {};
    const hasLoc = loc.district || loc.taluk || loc.hobli || loc.village;
    const payload = {
      ...formData,
      case_location: hasLoc ? loc : null
    };

    try {
      if (editingCase) {
        await api.put(`/cases/${editingCase.id}`, payload);
        toast.success('Case updated successfully');
      } else {
        const response = await api.post('/cases', payload);
        toast.success('Case created successfully');
        navigate(`/cases/${response.data.id}`);
      }
      setDialogOpen(false);
      fetchCases();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const openDeleteDialog = (caseItem) => {
    setDeleteTarget(caseItem);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/cases/${deleteTarget.id}`);
      toast.success('Case deleted');
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      fetchCases();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete case');
    } finally {
      setDeleting(false);
    }
  };

  async function handleRestoreCase(c) {
    setRestoringId(c.id);
    try {
      await api.post(`/cases/${c.id}/restore`);
      toast.success(`${c.case_name} restored`);
      fetchCases();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restore case');
    } finally {
      setRestoringId(null);
    }
  }

  const openEditDialog = (caseItem) => {
    setEditingCase(caseItem);
    setFormData({
      case_name: caseItem.case_name,
      client_name: caseItem.client_name,
      property_reference: caseItem.property_reference || '',
      report_date: caseItem.report_date || '',
      addressee_address: caseItem.addressee_address || '',
      status: caseItem.status,
      case_location: caseItem.case_location || { district: '', taluk: '', hobli: '', village: '' }
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingCase(null);
    setFormData({
      case_name: '',
      client_name: '',
      property_reference: '',
      report_date: '',
      addressee_address: '',
      status: 'active',
      case_location: { district: '', taluk: '', hobli: '', village: '' }
    });
  };

  const matchesSearch = (c) =>
    c.case_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.client_name.toLowerCase().includes(searchQuery.toLowerCase());

  const canEditCase = (c) => {
    if (!user) return false;
    if (c.deleted_at) return false;
    if (isAdmin) return true;
    if (c.created_by === user.id) return true;
    return (c.assigned_users || []).includes(user.id);
  };

  // Staff can delete cases they can edit; Super Admin can delete any active case
  const canDeleteCase = (c) => {
    if (!user) return false;
    if (c.deleted_at) return false;
    if (isAdmin) return true;
    if (c.created_by === user.id) return true;
    return (c.assigned_users || []).includes(user.id);
  };

  const activeCases = cases.filter((c) => !c.deleted_at);
  const deletedCases = cases.filter((c) => !!c.deleted_at);
  const myCases = activeCases.filter((c) => canEditCase(c) && matchesSearch(c));
  const otherCases = activeCases.filter((c) => !canEditCase(c) && matchesSearch(c));
  const deletedFiltered = deletedCases.filter(matchesSearch);

  const renderCaseCard = (caseItem) => {
    const editable = canEditCase(caseItem);
    const deletable = canDeleteCase(caseItem);
    const isDeleted = !!caseItem.deleted_at;
    return (
      <Card
        key={caseItem.id}
        className={`border-0 shadow-sm transition-shadow ${isDeleted ? 'opacity-75 border border-red-200' : 'hover:shadow-md'}`}
        data-testid={`case-card-${caseItem.id}`}
      >
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <Link
              to={`/cases/${caseItem.id}`}
              className="flex-1 flex items-start gap-4 group"
            >
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                isDeleted ? 'bg-red-100' : 'bg-[#1e3a5f]/10'
              }`}>
                <FolderOpen size={24} className={isDeleted ? 'text-red-500' : 'text-[#1e3a5f]'} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className={`font-semibold transition-colors ${
                    isDeleted ? 'text-slate-500 line-through' : 'text-slate-900 group-hover:text-[#1e3a5f]'
                  }`}>
                    {caseItem.case_name}
                  </h3>
                  {isDeleted ? (
                    <span
                      className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full"
                      data-testid={`badge-deleted-${caseItem.id}`}
                    >
                      <AlertCircle size={10} /> Deleted
                    </span>
                  ) : editable ? (
                    <span
                      className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full"
                      data-testid={`badge-editable-${caseItem.id}`}
                    >
                      <Pencil size={10} /> Editable
                    </span>
                  ) : (
                    <span
                      className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-slate-600 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-full"
                      data-testid={`badge-reference-${caseItem.id}`}
                    >
                      <EyeOff size={10} /> Reference Only
                    </span>
                  )}
                </div>
                <p className="text-sm text-slate-500 mt-1">{caseItem.client_name}</p>
                {caseItem.property_reference && (
                  <p className="text-xs text-slate-400 mt-1 truncate">
                    Ref: {caseItem.property_reference}
                  </p>
                )}
                <div className="flex items-center gap-x-4 gap-y-1 mt-3 flex-wrap">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium ${
                      isDeleted
                        ? 'bg-slate-100 text-slate-600'
                        : caseItem.status === 'active'
                        ? 'bg-green-100 text-green-700'
                        : caseItem.status === 'completed'
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    {caseItem.status}
                  </span>
                  {caseItem.created_by_name && (
                    <span className="text-xs text-slate-500" data-testid={`case-created-by-${caseItem.id}`}>
                      Created By: {caseItem.created_by_name}
                    </span>
                  )}
                  {caseItem.assigned_users_names?.length > 0 && (
                    <span
                      className="text-xs text-slate-500"
                      data-testid={`case-assigned-to-${caseItem.id}`}
                    >
                      Assigned To: {caseItem.assigned_users_names.join(', ')}
                    </span>
                  )}
                  <span className="text-xs text-slate-400">
                    Created: {new Date(caseItem.created_at).toLocaleDateString()}
                  </span>
                </div>
                {isDeleted && (
                  <p
                    className="text-xs text-red-600 mt-2"
                    data-testid={`case-deleted-info-${caseItem.id}`}
                  >
                    Deleted by{' '}
                    <span className="font-medium">{caseItem.deleted_by_name || 'Unknown'}</span>
                    {' '}on {new Date(caseItem.deleted_at).toLocaleString()}
                  </p>
                )}
              </div>
              <ArrowRight size={20} className="text-slate-300 group-hover:text-[#1e3a5f] transition-colors mt-2" />
            </Link>

            {isDeleted && isAdmin ? (
              <Button
                variant="outline"
                size="sm"
                className="ml-2"
                onClick={() => handleRestoreCase(caseItem)}
                disabled={restoringId === caseItem.id}
                data-testid={`restore-case-${caseItem.id}`}
              >
                <Undo2 size={14} className="mr-1.5" />
                {restoringId === caseItem.id ? 'Restoring…' : 'Restore'}
              </Button>
            ) : !isDeleted && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="ml-2" data-testid={`case-menu-${caseItem.id}`}>
                    <MoreVertical size={18} />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => navigate(`/cases/${caseItem.id}`)}>
                    <Eye size={16} className="mr-2" />
                    Open Case
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate(`/cases/${caseItem.id}/ownership-flow`)}
                    data-testid={`menu-view-flow-${caseItem.id}`}
                  >
                    <GitBranch size={16} className="mr-2" />
                    Ownership Flow (Manual)
                  </DropdownMenuItem>
                  {editable && (
                    <DropdownMenuItem onClick={() => openEditDialog(caseItem)} data-testid={`menu-edit-${caseItem.id}`}>
                      <Edit size={16} className="mr-2" />
                      Edit Case
                    </DropdownMenuItem>
                  )}
                  {editable && (
                    <DropdownMenuItem
                      onClick={() => { setAssignTarget(caseItem); setAssignDialogOpen(true); }}
                      data-testid={`menu-assign-${caseItem.id}`}
                    >
                      <UserCog size={16} className="mr-2" />
                      Assign Case
                    </DropdownMenuItem>
                  )}
                  {deletable && (
                    <DropdownMenuItem
                      onClick={() => openDeleteDialog(caseItem)}
                      className="text-red-600"
                      data-testid={`menu-delete-${caseItem.id}`}
                    >
                      <Trash2 size={16} className="mr-2" />
                      Delete Case
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-slate-200 rounded w-48" />
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 bg-slate-200 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cases-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1e3a5f]" style={{ fontFamily: 'Playfair Display, serif' }}>
            Cases
          </h1>
          <p className="text-slate-500 mt-1">Manage your property due diligence cases</p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <Select
              value={viewUserId || 'all'}
              onValueChange={(v) => setViewUserId(v === 'all' ? '' : v)}
            >
              <SelectTrigger className="w-[230px]" data-testid="user-wise-select">
                <div className="flex items-center gap-2 min-w-0">
                  <Users size={15} className="text-slate-400 shrink-0" />
                  <SelectValue placeholder="User-wise Cases" />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="user-wise-option-all">All cases</SelectItem>
                {usersList.map((u) => (
                  <SelectItem key={u.id} value={u.id} data-testid={`user-wise-option-${u.id}`}>
                    {u.name} ({u.role === 'super_admin' ? 'Admin' : 'Staff'})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {isAdmin && (
            <Button
              variant="outline"
              onClick={() => setShowDeleted((v) => !v)}
              data-testid="toggle-show-deleted-btn"
            >
              {showDeleted ? (
                <><EyeOff size={16} className="mr-2" /> Hide Deleted</>
              ) : (
                <><Eye size={16} className="mr-2" /> Show Deleted</>
              )}
            </Button>
          )}
          <Button 
            onClick={() => { resetForm(); setDialogOpen(true); }}
            className="bg-[#1e3a5f] hover:bg-[#162c46]"
            data-testid="create-case-btn"
          >
            <Plus size={18} className="mr-2" />
            New Case
          </Button>
        </div>
      </div>

      {/* Search */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
            <Input
              placeholder="Search cases by name or client..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-11"
              data-testid="search-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Cases Sections */}
      {viewUserId ? (
        <section data-testid="user-wise-cases-section" className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-semibold text-[#1e3a5f]" style={{ fontFamily: 'Playfair Display, serif' }}>
                Cases of {(usersList.find((u) => u.id === viewUserId) || {}).name || 'User'}
              </h2>
              <span className="text-xs text-slate-400">
                {cases.filter(matchesSearch).length} case{cases.filter(matchesSearch).length === 1 ? '' : 's'} belonging to this user (created or assigned)
              </span>
            </div>
            <Button variant="outline" onClick={() => setViewUserId('')} data-testid="back-to-all-cases-btn">
              <Undo2 size={16} className="mr-2" /> Back to all cases
            </Button>
          </div>
          {cases.filter(matchesSearch).length === 0 ? (
            <Card className="border-0 shadow-sm">
              <CardContent className="py-10 text-center text-sm text-slate-500">
                {searchQuery
                  ? 'No cases match your search for this user.'
                  : 'This user has no cases yet.'}
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {cases.filter(matchesSearch).map(renderCaseCard)}
            </div>
          )}
        </section>
      ) : cases.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <CardContent className="py-12">
            <div className="text-center">
              <FolderOpen size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500 mb-4">
                {searchQuery ? 'No cases found matching your search.' : 'No cases yet. Create your first case to get started.'}
              </p>
              {!searchQuery && (
                <Button
                  onClick={() => { resetForm(); setDialogOpen(true); }}
                  className="bg-[#1e3a5f] hover:bg-[#162c46]"
                >
                  <Plus size={18} className="mr-2" />
                  Create Case
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* My Cases */}
          <section data-testid="my-cases-section">
            <div className="flex items-baseline gap-3 mb-3">
              <h2
                className="text-xl font-semibold text-[#1e3a5f]"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                My Cases
              </h2>
              <span className="text-xs text-slate-400">
                {myCases.length} case{myCases.length === 1 ? '' : 's'} you can edit
              </span>
            </div>
            {myCases.length === 0 ? (
              <Card className="border-0 shadow-sm">
                <CardContent className="py-8 text-center text-sm text-slate-500">
                  {searchQuery
                    ? 'No assigned cases match your search.'
                    : 'No cases assigned to you yet. Create a new case or ask an admin to assign one.'}
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {myCases.map(renderCaseCard)}
              </div>
            )}
          </section>

          {/* All Cases (Reference) */}
          <section data-testid="all-cases-section" className="pt-2">
            <div className="flex items-baseline gap-3 mb-3">
              <h2
                className="text-xl font-semibold text-slate-700"
                style={{ fontFamily: 'Playfair Display, serif' }}
              >
                All Cases <span className="text-slate-400 font-normal">(Reference)</span>
              </h2>
              <span className="text-xs text-slate-400">
                {otherCases.length} case{otherCases.length === 1 ? '' : 's'} from other users — view & download only
              </span>
            </div>
            {otherCases.length === 0 ? (
              <Card className="border-0 shadow-sm">
                <CardContent className="py-8 text-center text-sm text-slate-500">
                  {searchQuery
                    ? 'No reference cases match your search.'
                    : 'No reference cases yet. Cases from other users will appear here.'}
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {otherCases.map(renderCaseCard)}
              </div>
            )}
          </section>
          {/* Deleted Cases (Super Admin only, when Show Deleted is on) */}
          {isAdmin && showDeleted && (
            <section data-testid="deleted-cases-section" className="pt-2">
              <div className="flex items-baseline gap-3 mb-3">
                <h2
                  className="text-xl font-semibold text-red-700"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  Deleted Cases
                </h2>
                <span className="text-xs text-slate-400">
                  {deletedFiltered.length} soft-deleted case{deletedFiltered.length === 1 ? '' : 's'} — restorable
                </span>
              </div>
              {deletedFiltered.length === 0 ? (
                <Card className="border-0 shadow-sm">
                  <CardContent className="py-8 text-center text-sm text-slate-500">
                    {searchQuery
                      ? 'No deleted cases match your search.'
                      : 'No deleted cases.'}
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {deletedFiltered.map(renderCaseCard)}
                </div>
              )}
            </section>
          )}
        </>
      )}

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete this case?</DialogTitle>
            <DialogDescription>
              <span className="font-medium text-slate-900">{deleteTarget?.case_name}</span>
              {deleteTarget?.client_name ? ` — ${deleteTarget.client_name}` : ''}
              <br /><br />
              The case will be <b>soft-deleted</b> — all document entries and uploaded
              files are preserved. A record of <i>who deleted it and when</i> is kept.
              Only a Super Admin can restore it later from the &quot;Show Deleted&quot; view.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleting}
              data-testid="cancel-delete-case-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-case-btn"
            >
              {deleting ? 'Deleting…' : 'Delete Case'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Case Dialog */}
      <AssignCaseDialog
        open={assignDialogOpen}
        onOpenChange={setAssignDialogOpen}
        caseItem={assignTarget}
        onSaved={() => fetchCases()}
      />

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
              {editingCase ? 'Edit Case' : 'Create New Case'}
            </DialogTitle>
            <DialogDescription>
              {editingCase ? 'Update the case details below.' : 'Enter the details for your new case.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="case_name">Village Name *</Label>
                <Input
                  id="case_name"
                  value={formData.case_name}
                  onChange={(e) => setFormData({ ...formData, case_name: e.target.value })}
                  placeholder="e.g., Harohalli"
                  required
                  data-testid="case-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="client_name">Client Name *</Label>
                <Input
                  id="client_name"
                  value={formData.client_name}
                  onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                  placeholder="e.g., Mr. John Doe"
                  required
                  data-testid="client-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="property_reference">Property Reference *</Label>
                <Input
                  id="property_reference"
                  value={formData.property_reference}
                  onChange={(e) => setFormData({ ...formData, property_reference: e.target.value })}
                  placeholder="e.g., Sy. No. 123, Village XYZ"
                  required
                  data-testid="property-ref-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="report_date">Report Date</Label>
                <Input
                  id="report_date"
                  value={formData.report_date}
                  onChange={(e) => setFormData({ ...formData, report_date: e.target.value })}
                  placeholder="e.g., November 01, 2025"
                  data-testid="report-date-input"
                />
                <p className="text-xs text-slate-500">Shown as the date at the top of the DDR. Leave blank to use today's date.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="addressee_address">Addressee Address</Label>
                <Textarea
                  id="addressee_address"
                  value={formData.addressee_address}
                  onChange={(e) => setFormData({ ...formData, addressee_address: e.target.value })}
                  placeholder={"83, 1st Floor, above Bank of India,\nVV Puram,\nBengaluru – 560 004"}
                  rows={3}
                  data-testid="addressee-address-input"
                />
                <p className="text-xs text-slate-500">Appears under "To, {`{Client Name}`}" in the DDR. One line per row.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger data-testid="status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="pt-2 border-t border-slate-100">
                <CaseLocationFields
                  value={formData.case_location}
                  onChange={(loc) => setFormData(prev => ({ ...prev, case_location: loc }))}
                />
                <p className="text-xs text-slate-400 mt-2">
                  This location will auto-fill in all document forms under this case.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-[#1e3a5f] hover:bg-[#162c46]"
                disabled={submitting}
                data-testid="submit-case-btn"
              >
                {submitting ? 'Saving...' : (editingCase ? 'Update Case' : 'Create Case')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CasesPage;
